import { useState, type CSSProperties } from 'react';
import { color, priorityTheme } from '../styles/theme';
import { addBusinessDays, dueLabel, siteToday } from '../lib/deadlines';
import { fieldErrorsFrom } from '../lib/fieldErrors';
import { ApiError, type TicketPriority } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useMeta, usePeople } from '../api/queries';
import { PRIORITY_GUIDE, PRIORITY_LABEL, useAddTicketFile, useCreateTicket } from '../api/tickets';
import { Button, Card, CardTitle, ErrorNote, Field, inputStyle, selectStyle } from '../components/ui';
import { PersonSelect } from './MeetingBits';
import { PriorityPill } from './TicketList';
import { AttachPicker } from './TicketBits';

/**
 * New ticket: what you need, how urgent it is, and who should take it.
 *
 * The priority fills the due date — the same rule the API applies, business
 * days on the site's calendar with Yale's holidays skipped — and the date
 * stays editable. Filed in the person's own name; nothing else is asked of
 * them. Owner, priority and due date are the queue's to set (tickets:update):
 * a role without it does not see them, and its request carries none of them,
 * so the API files it unassigned at normal priority, dated by that priority
 * (it answers 403 to anything more). Assign to lists every active account
 * (GET /admins/people), whether or not the person can read meetings (QA
 * C-TIC-036). Files picked here go up once the ticket
 * exists; one that fails stays picked, and the ticket is never filed twice.
 */

const PRIORITIES: readonly TicketPriority[] = ['critical', 'normal', 'low'];

const label: CSSProperties = { fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.muted, marginBottom: 6 };

export function NewTicket({ onCreated }: { onCreated: (id: string) => void }) {
  const { admin, can } = useAuth();
  const canRunQueue = can('tickets:update');
  const meta = useMeta();
  const people = usePeople(canRunQueue);
  const create = useCreateTicket();
  const addFile = useAddTicketFile();

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [dueOn, setDueOn] = useState<string>(addBusinessDays(siteToday(), PRIORITY_GUIDE.normal.days));
  const [dueTouched, setDueTouched] = useState(false);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [study, setStudy] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<File[]>([]);
  /** Set once the ticket exists but a file did not go up, so a retry attaches rather than files again. */
  const [filed, setFiled] = useState<{ id: string; ref: string } | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  function pickPriority(p: TicketPriority) {
    setPriority(p);
    if (!dueTouched) setDueOn(addBusinessDays(siteToday(), PRIORITY_GUIDE[p].days));
  }

  const ready = title.trim().length > 0 && !create.isPending && !progress;

  async function submit() {
    setErrors({});
    setAttachError(null);
    let ticket = filed;
    if (!ticket) {
      try {
        const res = await create.mutateAsync({
          title: title.trim(),
          details: details.trim() || null,
          studySlug: study || null,
          // Owner, priority and due date only from someone who runs the queue; the API refuses them from anyone else.
          ...(canRunQueue
            ? {
                priority,
                ownerId,
                // Only a date the person chose goes up; otherwise the API works it out on its own calendar, holidays included.
                ...(dueTouched ? { dueOn: dueOn || null } : {}),
              }
            : {}),
        });
        ticket = { id: res.ticket.id, ref: res.ticket.ref };
      } catch (e) {
        if (e instanceof ApiError) setErrors(fieldErrorsFrom(e.details));
        return;
      }
    }
    const queue = [...picked];
    for (let i = 0; i < queue.length; i++) {
      const file = queue[i]!;
      setProgress(queue.length > 1 ? `Attaching ${i + 1} of ${queue.length}…` : 'Attaching…');
      try {
        await addFile.mutateAsync({ id: ticket.id, file });
        setPicked((prev) => prev.filter((f) => f !== file));
      } catch (e) {
        setProgress(null);
        setFiled(ticket);
        setAttachError(
          `The ticket was filed as ${ticket.ref}, but ${file.name} could not be attached: ${e instanceof Error ? e.message : 'something went wrong.'} Try again, or open the ticket without it.`,
        );
        return;
      }
    }
    setProgress(null);
    onCreated(ticket.id);
  }

  return (
    <div className="meeting-cols">
      <Card>
        <CardTitle style={{ marginBottom: 12 }}>What do you need?</CardTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field label="In one line" error={errors.title}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. The fourth-floor printer is out of toner"
              style={inputStyle}
              autoFocus
            />
          </Field>
          <Field label="Details" hint="What is happening, what you tried, what would help. Refer to a participant by record ID, never by name.">
            <textarea
              value={details}
              rows={6}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Describe it…"
              style={{ ...inputStyle, fontSize: 14, lineHeight: '21px', resize: 'vertical' }}
            />
          </Field>

          {/* Not a Field: a <label> around the picker would send every click to its hidden input. */}
          <div>
            <div style={label}>Files</div>
            <AttachPicker files={picked} onChange={setPicked} have={0} disabled={!!progress} />
            <p style={{ fontSize: 12.5, color: color.muted, margin: '6px 0 0' }}>Optional — the invoice, a screenshot, the quote.</p>
          </div>

          {canRunQueue && (
            <Field label="How urgent">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {PRIORITIES.map((p) => (
                  <label key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: color.ink, cursor: 'pointer' }}>
                    <input type="radio" name="priority" checked={priority === p} onChange={() => pickPriority(p)} style={{ accentColor: color.blue, marginTop: 3 }} />
                    <span aria-hidden style={{ width: 10, height: 10, borderRadius: 999, background: priorityTheme[p].fg, flex: 'none', marginTop: 5 }} />
                    <span>
                      <strong>{PRIORITY_LABEL[p]}</strong>
                      <span style={{ display: 'block', fontSize: 12.5, color: color.muted }}>{PRIORITY_GUIDE[p].line}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Field>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {canRunQueue && (
              <Field label="Due" hint={dueTouched ? 'Your date.' : 'From the priority — change it if you like.'} error={errors.dueOn}>
                <input
                  type="date"
                  value={dueOn}
                  onChange={(e) => {
                    setDueOn(e.target.value);
                    setDueTouched(true);
                  }}
                  style={inputStyle}
                />
              </Field>
            )}
            <Field label="Study" hint="Optional — a label, not a scope.">
              <select value={study} onChange={(e) => setStudy(e.target.value)} style={selectStyle}>
                <option value="">None</option>
                {(meta.data?.studies ?? []).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.short}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {canRunQueue ? (
            <Field label="Assign to" hint="Optional — leave it for anyone to pick up." error={errors.ownerId}>
              {people.data ? (
                <PersonSelect label="Owner" value={ownerId} currentName={null} people={people.data.people} attendees={[]} onChange={setOwnerId} />
              ) : (
                <span style={{ fontSize: 12.5, color: color.muted }}>
                  {people.isLoading
                    ? 'Loading people…'
                    : people.error
                      ? `The list of people did not load (${(people.error as Error).message}). File it unassigned and set the owner on the ticket.`
                      : 'Leave it for anyone to pick up.'}
                </span>
              )}
            </Field>
          ) : (
            <p style={{ fontSize: 12.5, color: color.muted, margin: 0 }}>
              Whoever runs the ticket queue sets the owner, priority and due date.
            </p>
          )}

          {create.error && !(create.error instanceof ApiError && create.error.status === 400) && (
            <ErrorNote message={(create.error as Error).message} />
          )}
          {attachError && filed && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
              <ErrorNote message={attachError} />
              <Button variant="ghost" style={{ padding: '5px 11px', fontSize: 12 }} onClick={() => onCreated(filed.id)}>
                Open the ticket without it
              </Button>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderTop: `1px solid ${color.line}`, paddingTop: 14 }}>
            <span style={{ fontSize: 12.5, color: color.muted, flex: 1, minWidth: 220 }}>
              Filing as <strong style={{ color: color.ink }}>{admin?.name ?? 'you'}</strong>
              {canRunQueue && (
                <>
                  {' '}
                  · {PRIORITY_LABEL[priority].toLowerCase()} priority
                  {dueOn ? ` · due ${dueLabel(dueOn)}` : ''}
                </>
              )}
            </span>
            <Button variant="navy" onClick={() => void submit()} disabled={!ready} title={ready ? undefined : 'Say what you need first.'}>
              {progress ?? (create.isPending ? 'Filing…' : filed ? 'Try attaching again' : 'File the ticket')}
            </Button>
          </div>
        </div>
      </Card>

      {/* The guide is the one thing on this page worth reading before filing, so it sits on the tinted callout surface the other screens use. */}
      <Card style={{ background: color.tint, borderColor: color.tint2 }}>
        <CardTitle style={{ marginBottom: 6 }}>How due dates work</CardTitle>
        <p style={{ fontSize: 13, color: color.ink, margin: '0 0 10px', lineHeight: '19px' }}>
          The priority sets the due date by business days on the lab's calendar — weekends and Yale holidays are skipped.
          {canRunQueue
            ? ' Pick your own date if the request has one.'
            : ' Whoever runs the ticket queue picks the priority, and can set a date of its own.'}
        </p>
        {PRIORITIES.map((p) => (
          <div key={p} style={{ display: 'grid', gridTemplateColumns: '92px minmax(0,1fr)', gap: 10, alignItems: 'start', padding: '9px 0', borderTop: `1px solid ${color.tint2}`, fontSize: 13 }}>
            <span style={{ paddingTop: 1 }}>
              <PriorityPill priority={p} />
            </span>
            <span style={{ color: color.ink }}>{PRIORITY_GUIDE[p].line}</span>
          </div>
        ))}
        <p style={{ fontSize: 12.5, color: color.ink, margin: '12px 0 0', lineHeight: '18px' }}>
          Once filed, the ticket appears under <strong>My requests</strong>, and under <strong>My action items</strong> for whoever takes it.
          Anyone can add an update to it.
        </p>
      </Card>
    </div>
  );
}
